import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Button from 'client/components/aria/Button/Button';
import Table from 'client/components/aria/Table';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';

/**
 * CONTACT DETAILS RECONCILIATION PROCESS:
 *
 * This component receives two separate data sources:
 *
 * 1. affectedAssetOwners (from EnquiryRequestService.getAffectedAssetOwners)
 *    - List of organisations with assets in the enquiry area
 *    - Contains organisation IDs and names
 *    - DOES NOT contain contact details
 *
 * 2. assetOwners (from AssetOwnerService.getAssetOwners)
 *    - Contains two arrays: operationalZone and assetsInAOIOwners
 *    - Each organisation record INCLUDES contact details (business & emergency)
 *    - May use different identifier fields (organisationId, dataProviderId, etc.)
 *
 * The challenge: Match organisations from affectedAssetOwners with their contact details in assetOwners
 *
 * Solution: The findContactSourceForOwner() function attempts to match organisations by:
 * - Extracting all possible identifier fields from both sources
 * - Normalising identifiers (lowercase, trimmed)
 * - Finding matches based on any overlapping identifier
 *
 * This reconciliation is necessary because:
 * - Different APIs may use different field names for the same data
 * - Organisation names can be inconsistent across systems
 * - ID-based matching is more reliable than name-based matching
 */

// Normalise potential identifiers so we can compare strings returned by different APIs
const normaliseIdentifier = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const trimmedValue = String(value).trim();
    return trimmedValue.length ? trimmedValue.toLowerCase() : null;
};

// Collect any plausible ID fields to increase our chances of matching assets across services
const extractOwnerIdentifiers = (owner) => (
    [
        owner?.organisationId,
        owner?.organisationID,
        owner?.id,
        owner?.dataProviderId,
        owner?.dataProviderID
    ]
        .map(normaliseIdentifier)
        .filter(Boolean)
);

// Guarantee the modal always receives arrays even if the API omits a contact type
const ensureContactsShape = (contacts = {}) => ({
    business: Array.isArray(contacts.business) ? contacts.business : [],
    emergency: Array.isArray(contacts.emergency) ? contacts.emergency : []
});

// Asset owners are fetched from multiple sources; reconcile using identifiers only so we avoid mixing similarly named organisations
const findContactSourceForOwner = (owner, assetOwners = {}) => {
    if (!owner) {
        return null;
    }

    const assetOwnerSources = [
        ...(assetOwners.operationalZone ?? []),
        ...(assetOwners.assetsInAOIOwners ?? [])
    ].filter(Boolean);

    const ownerIdentifiers = extractOwnerIdentifiers(owner);

    if (!ownerIdentifiers.length) {
        return null;
    }

    return assetOwnerSources.find(candidate => {
        const candidateIdentifiers = extractOwnerIdentifiers(candidate);
        return candidateIdentifiers.some(id => ownerIdentifiers.includes(id));
    });
};

// Derive a label regardless of which field happens to be populated
const getOwnerDisplayName = (owner, fallbackLabel) => {
    const potentialNames = [
        owner?.name,
        owner?.organisationName,
        owner?.displayName,
        owner?.label,
        owner?.organisationId
    ];

    const displayName = potentialNames.find(value => typeof value === 'string' && value.trim().length);
    return displayName ?? fallbackLabel;
};

const AffectedAssetOwnersNotification = ({
    affectedAssetOwners,
    assetOwners,
    isEmergency,
    onShowContactDetails
}) => {
    const intl = useIntl();
    const notAvailableLabel = intl.formatMessage({ id: 'viewEnquiryPage.notification.table.notAvailable' });

    return (
        <div
            aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notification.title' })}
            data-testid="notificationSummary"
        >
            <Table
                aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notification.title' })}
                data-testid="notificationSummary.table"
                className="govuk-!-margin-bottom-0"
            >
                <TableHeader className="govuk-!-margin-bottom-4">
                    <Column>
                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.organisations' })}
                    </Column>
                    <Column className="govuk-!-text-align-right">
                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.contactDetails' })}
                    </Column>
                </TableHeader>
                <TableBody>
                    {affectedAssetOwners.map((owner, index) => {
                        const ownerKey = String(owner?.id ?? owner?.organisationId ?? owner?.organisationName ?? index);

                        // Find the matching organisation record from assetOwners that contains contact details
                        // This reconciles the affected asset owner (which has no contacts) with the asset owner data (which has contacts)
                        const contactSource = findContactSourceForOwner(owner, assetOwners);

                        // Extract and normalise contacts from the matched source
                        // Ensures we always have business and emergency arrays even if the API omits them
                        const contacts = ensureContactsShape(contactSource?.contacts ?? {});

                        // Use the contact source if found, otherwise fallback to the original owner record
                        const contactOwnerRecord = contactSource ?? owner;
                        const organisationLabel = getOwnerDisplayName(contactOwnerRecord, notAvailableLabel);

                        return (
                            <Row key={ownerKey}>
                                <Cell>
                                    <div data-testid={`notificationSummary.item.${ownerKey}`}>
                                        <span className="govuk-body govuk-!-font-weight-bold">
                                            {organisationLabel}
                                        </span>
                                    </div>
                                </Cell>
                                <Cell horzAlign="right">
                                    <Button
                                        variant="secondary"
                                        onPress={() => {
                                            onShowContactDetails({
                                                organisationName: organisationLabel,
                                                contacts,
                                                contacted: undefined
                                            });
                                        }}
                                        className="govuk-!-margin-top-2 govuk-!-margin-bottom-2"
                                        data-testid={`notificationSummary.contactDetailsButton.${ownerKey}`}
                                    >
                                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.table.viewContact' })}
                                    </Button>
                                </Cell>
                            </Row>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

AffectedAssetOwnersNotification.propTypes = {
    affectedAssetOwners: PropTypes.arrayOf(PropTypes.object),
    assetOwners: PropTypes.shape({
        operationalZone: PropTypes.array
    }),
    isEmergency: PropTypes.bool,
    onShowContactDetails: PropTypes.func.isRequired
};

AffectedAssetOwnersNotification.defaultProps = {
    affectedAssetOwners: [],
    assetOwners: {
        operationalZone: []
    },
    isEmergency: false
};

export default AffectedAssetOwnersNotification;
